import { useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { Switch } from "../../Switch/Switch";
import { Button } from "../../Button/Button";
import { Input } from "../../Input/Input";
import { Typography } from "../../Typography/Typography";
import { useMutation, useQuery } from "@apollo/client";
import { useOptionalCurrentUser } from "../../../utils/hooks";
import {
  GET_CURRENT_USER_SETTINGS,
  UPDATE_USER_SETTINGS,
} from "../graphql";

const { Text } = Typography;
const { TextArea } = Input;

export function MemoryTab() {
  const currentUser = useOptionalCurrentUser();
  const { data, loading, refetch } = useQuery(GET_CURRENT_USER_SETTINGS, {
    fetchPolicy: "cache-and-network",
  });
  const [updateSettings, { loading: saving }] = useMutation(
    UPDATE_USER_SETTINGS
  );
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");

  if (loading) return <CircularProgress size={16} />;

  const settings = data?.currentUserSettings;
  const chatPrefs = settings?.data?.chatPreferences;
  // Auto-enabled by default — only disabled if user explicitly turned it off
  const enabled = chatPrefs?.enabled !== false;
  const preferences = chatPrefs?.preferences ?? "";
  const userId = settings?.userId ?? currentUser?.id;

  const handleToggle = async (checked: boolean) => {
    if (userId == null) return; // no user yet — nothing to persist against
    await updateSettings({
      variables: {
        input: {
          userId,
          data: {
            chatPreferences: {
              enabled: checked,
              preferences: preferences || undefined,
              lastUpdated: new Date().toISOString(),
            },
          },
        },
      },
    });
    refetch();
  };

  const handleSave = async () => {
    if (userId == null) return; // no user yet — nothing to persist against
    await updateSettings({
      variables: {
        input: {
          userId,
          data: {
            chatPreferences: {
              enabled,
              preferences: editText,
              lastUpdated: new Date().toISOString(),
            },
          },
        },
      },
    });
    setEditing(false);
    refetch();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Text>AI can learn preferences</Text>
        <Switch
          checked={enabled}
          onChange={handleToggle}
          loading={saving}
          data-cy="memory-toggle"
        />
      </div>
      <Text type="secondary" style={{ fontSize: 11 }}>
        {enabled
          ? "The AI will remember preferences you share during conversations."
          : "Memory is off. The AI won't save new preferences."}
      </Text>
      <div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Your preferences:
        </Text>
        {editing ? (
          <div style={{ marginTop: 4 }}>
            <TextArea
              value={editText}
              onChange={e => setEditText(e.target.value)}
              autoSize={{ minRows: 3, maxRows: 6 }}
              maxLength={1000}
              placeholder="e.g., I prefer data in table format. Call me Sam. I work on the private equity team."
            />
            <div
              style={{
                marginTop: 4,
                display: "flex",
                gap: 4,
                justifyContent: "flex-end",
              }}
            >
              <Button size="small" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button
                size="small"
                type="primary"
                onClick={handleSave}
                loading={saving}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 4 }}>
            <Text
              style={{
                fontSize: 12,
                whiteSpace: "pre-wrap",
                display: "block",
                padding: "4px 8px",
                background: "#f5f5f5",
                borderRadius: 4,
                minHeight: 32,
              }}
            >
              {preferences || "No preferences saved yet. Chat with the AI and share your preferences, or edit manually."}
            </Text>
            <Button
              size="small"
              style={{ marginTop: 4 }}
              onClick={() => {
                setEditText(preferences);
                setEditing(true);
              }}
            >
              Edit
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
